// Web Share Target support. Android/Chrome navigates an installed PWA to
// /share?title=...&text=...&url=... (per share_target in manifest.webmanifest).
// The share can arrive before login, so the payload is stashed in sessionStorage
// and survives the OAuth round-trip; App consumes it once the user is known.

const STORAGE_KEY = 'nr-share-prefill';

export interface SharePrefill {
  title: string;
  body: string;
}

export function buildSharePrefill(params: URLSearchParams): SharePrefill | null {
  const sTitle = (params.get('title') ?? '').trim();
  const sText = (params.get('text') ?? '').trim();
  const sUrl = (params.get('url') ?? '').trim();
  if (!sTitle && !sText && !sUrl) return null;

  let title = sTitle;
  let body = sText;
  if (!title && sText) {
    const [firstLine = '', ...rest] = sText.split('\n');
    if (firstLine.length > 140) {
      title = firstLine.slice(0, 140);
      body = sText;
    } else {
      title = firstLine;
      body = rest.join('\n').trim();
    }
  }
  if (!title) title = sUrl;
  if (sUrl && sUrl !== title) {
    body = body ? `${body}\n\n${sUrl}` : sUrl;
  }
  return { title, body };
}

// Runs before React mounts: capture the share payload, then normalize the URL
// so the rest of the app never sees /share.
export function interceptShareTarget(): void {
  if (window.location.pathname !== '/share') return;
  const prefill = buildSharePrefill(new URLSearchParams(window.location.search));
  if (prefill) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(prefill));
    } catch {
      // Storage unavailable (private mode quota) — the share is dropped,
      // but the app still opens rather than erroring.
    }
  }
  history.replaceState(null, '', '/app');
}

export function consumeSharePrefill(): SharePrefill | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as SharePrefill;
    if (typeof parsed.title !== 'string' || typeof parsed.body !== 'string') return null;
    return parsed.title || parsed.body ? parsed : null;
  } catch {
    return null;
  }
}

/** True while a share payload is waiting to be consumed (first-run steps aside for it). */
export function hasSharePrefill(): boolean {
  try {
    return sessionStorage.getItem(STORAGE_KEY) !== null;
  } catch {
    return false;
  }
}
